import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import Breadcrumbs from "@/components/base/Breadcrumbs";
import { useSeo } from "@/hooks/useSeo";
import { useFormSubmit } from "@/hooks/useFormSubmit";
import { site } from "@/mocks/site";

const CONTACT_ADDR = "https://readdy.ai/api/form/dah8cr3p14a1h3mr6f7g";
const MAX_LENGTH = 500;

const inputClass =
  "w-full rounded-md border border-background-300 bg-background-50 px-3.5 py-2.5 text-sm text-foreground-900 placeholder:text-foreground-400 focus:border-primary-400 focus:outline-none focus:ring-1 focus:ring-primary-300";
const labelClass = "mb-1.5 block text-sm font-label font-medium text-foreground-800";

const channels = [
  {
    icon: "ri-mail-line",
    title: "通用邮箱",
    value: site.email,
    text: "合作咨询、内容建议与一般问题。",
  },
  {
    icon: "ri-inbox-archive-line",
    title: "投稿专用",
    value: site.tipEmail,
    text: "材料补充、线索跟进与核实反馈。",
  },
  {
    icon: "ri-time-line",
    title: "受理时间",
    value: site.workTime,
    text: site.reviewCycle,
  },
];

function ContactPage() {
  const [messageLength, setMessageLength] = useState(0);
  const { status, message, submit, reset } = useFormSubmit(
    CONTACT_ADDR,
    "信息已发送，感谢你的反馈。我们会尽快查看，并通过你留下的邮箱回复。"
  );

  const jsonLd = useMemo(
    () => ({
      "@context": "https://schema.org",
      "@type": "ContactPage",
      name: "联系我们 - 爆料平台",
      description: "通过联系入口提交合作咨询、更正申请或补充说明，也可以直接发送邮件。",
      url: "https://gregbennettguitars.com.cn/contact",
      inLanguage: "zh-CN",
      mainEntity: {
        "@type": "Organization",
        name: site.name,
        email: site.email,
      },
    }),
    []
  );

  useSeo({
    title: "联系我们 - 合作咨询与更正反馈入口 | 爆料平台",
    description:
      "联系爆料平台：提交合作咨询、更正申请或补充说明，也可以通过邮箱直接沟通。被提及的一方可在此申请更新原文并保留修改记录。",
    keywords: "联系我们,更正申请,补充说明,合作咨询,爆料平台",
    path: "/contact",
    jsonLd,
  });

  return (
    <div className="w-full bg-background-50">
      <div className="border-b border-background-200 bg-background-100">
        <div className="w-full px-4 py-10 md:px-8 md:py-14">
          <Breadcrumbs items={[{ label: "首页", to: "/" }, { label: "联系我们" }]} />
          <h1 className="mt-4 font-heading text-3xl font-bold leading-tight text-foreground-950 md:text-5xl">
            联系我们
          </h1>
          <p className="mt-4 max-w-3xl text-sm leading-relaxed text-foreground-600 md:text-base">
            合作咨询、更正申请与补充说明，都可以通过下面的方式联系到我们。被提及的一方也可以申请更新原文，我们会保留修改记录。
          </p>
        </div>
      </div>

      <div className="w-full px-4 py-12 md:px-8 md:py-16">
        <div className="grid grid-cols-1 gap-10 lg:grid-cols-12 lg:gap-14">
          <div className="lg:col-span-5">
            <h2 className="font-heading text-xl font-bold text-foreground-950 md:text-2xl">
              联系渠道
            </h2>
            <div className="mt-6 space-y-4">
              {channels.map((channel) => (
                <div
                  key={channel.title}
                  className="flex items-start gap-4 rounded-lg border border-background-200 bg-background-100 p-5"
                >
                  <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-md bg-primary-100 text-primary-700">
                    <i className={`${channel.icon} text-xl`} />
                  </span>
                  <div className="min-w-0">
                    <h3 className="font-heading text-sm font-bold text-foreground-950">
                      {channel.title}
                    </h3>
                    <p className="mt-1 break-all text-sm font-label text-foreground-800">
                      {channel.value}
                    </p>
                    <p className="mt-1 text-xs leading-relaxed text-foreground-500">{channel.text}</p>
                  </div>
                </div>
              ))}
            </div>

            <div className="mt-6 rounded-lg border border-accent-300 bg-accent-50 p-6">
              <h3 className="flex items-center gap-2 font-heading text-base font-bold text-foreground-950">
                <i className="ri-shield-check-line text-accent-600" />
                更正与补充说明
              </h3>
              <p className="mt-2 text-sm leading-relaxed text-foreground-700">
                如果你是某条线索中被提及的一方，可在表单中注明线索标题，并附上你的说明或依据。核实后我们会更新原文，并标注修改时间。
              </p>
            </div>

            <p className="mt-6 text-sm text-foreground-500">
              想直接提交材料？
              <Link to="/submit" className="link-underline ml-1 font-medium text-primary-600">
                前往匿名爆料入口
              </Link>
            </p>
          </div>

          <div className="lg:col-span-7">
            {status === "success" ? (
              <div className="rounded-lg border border-secondary-300 bg-secondary-50 p-8 text-center">
                <span className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-secondary-100 text-secondary-700">
                  <i className="ri-checkbox-circle-line text-3xl" />
                </span>
                <h2 className="mt-5 font-heading text-xl font-bold text-foreground-950">发送成功</h2>
                <p className="mx-auto mt-3 max-w-md text-sm leading-relaxed text-foreground-700">
                  {message}
                </p>
                <div className="mt-6 flex flex-col justify-center gap-3 sm:flex-row">
                  <button
                    type="button"
                    onClick={reset}
                    className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-label font-medium text-background-50 transition-colors hover:bg-primary-600"
                  >
                    <i className="ri-add-line" />
                    再发一条
                  </button>
                  <Link
                    to="/"
                    className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md border border-background-300 px-5 py-2.5 text-sm font-label font-medium text-foreground-800 transition-colors hover:bg-background-50"
                  >
                    返回首页
                  </Link>
                </div>
              </div>
            ) : (
              <form
                data-readdy-form
                onSubmit={submit}
                className="rounded-lg border border-background-200 bg-background-50 p-6 md:p-8"
              >
                <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
                  <div>
                    <label htmlFor="name" className={labelClass}>
                      怎么称呼你
                    </label>
                    <input
                      id="name"
                      name="name"
                      type="text"
                      required
                      placeholder="称呼或昵称均可"
                      className={inputClass}
                    />
                  </div>
                  <div>
                    <label htmlFor="email" className={labelClass}>
                      邮箱
                    </label>
                    <input
                      id="email"
                      name="email"
                      type="email"
                      required
                      placeholder="用于接收我们的回复"
                      className={inputClass}
                    />
                  </div>
                </div>

                <div className="mt-5">
                  <label htmlFor="subject" className={labelClass}>
                    事由类型
                  </label>
                  <select id="subject" name="subject" defaultValue="合作咨询" className={inputClass}>
                    <option value="合作咨询">合作咨询</option>
                    <option value="更正申请">更正申请</option>
                    <option value="补充说明">补充说明</option>
                    <option value="内容建议">内容建议</option>
                    <option value="其他反馈">其他反馈</option>
                  </select>
                </div>

                <div className="mt-5">
                  <div className="flex items-center justify-between">
                    <label htmlFor="message" className={labelClass}>
                      留言内容
                    </label>
                    <span className="mb-1.5 text-xs text-foreground-400">
                      {messageLength}/{MAX_LENGTH}
                    </span>
                  </div>
                  <textarea
                    id="message"
                    name="message"
                    required
                    rows={7}
                    maxLength={MAX_LENGTH}
                    onChange={(event) => setMessageLength(event.target.value.length)}
                    placeholder="请说明你的来意。如果是更正或补充说明，建议注明相关线索标题与你的依据。"
                    className={`${inputClass} resize-none leading-relaxed`}
                  />
                </div>

                <div className="honeypot-field">
                  <input
                    type="text"
                    name="website_alt"
                    tabIndex={-1}
                    autoComplete="off"
                    aria-hidden="true"
                    readOnly
                  />
                </div>

                {status === "error" && (
                  <p className="mt-5 flex items-start gap-2 rounded-md border border-primary-300 bg-primary-50 px-4 py-3 text-sm text-primary-800">
                    <i className="ri-error-warning-line mt-0.5" />
                    {message}
                  </p>
                )}

                <div className="mt-7 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                  <p className="text-xs leading-relaxed text-foreground-500">
                    我们仅将你的邮箱用于回复本次咨询。
                  </p>
                  <button
                    type="submit"
                    disabled={status === "submitting"}
                    className="inline-flex shrink-0 items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-6 py-3 text-sm font-label font-medium text-background-50 transition-colors hover:bg-primary-600 disabled:cursor-not-allowed disabled:opacity-60"
                  >
                    {status === "submitting" ? (
                      <>
                        <i className="ri-loader-4-line animate-spin" />
                        正在发送
                      </>
                    ) : (
                      <>
                        <i className="ri-send-plane-fill" />
                        发送信息
                      </>
                    )}
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default ContactPage;